// ─────────────────────────────────────────────────────────────────────────────
// SHARED: Auth session  (Firebase Auth state, used by every feature)
// Provides the sign-in state via AuthContext (utils/authContext.ts, read with hooks/useAuth.ts).
// Firebase Auth is a large chunk, so it is only loaded for returning users (entryGate auth hint)
// or after a fresh sign-in — never for a first-time visitor just browsing.
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import type { ConfirmationResult, User } from "firebase/auth";
import { AuthContext, type AuthContextValue, type AuthStatus, type AuthUser } from "../utils/authContext";
import { clearAuthHint, hasAuthHint, setAuthHint } from "../utils/entryGate";
import { getFirebaseAuth } from "../utils/firebaseAuth";
import { isStaffRole, staffEmail } from "../utils/staffAuth";

interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
}

// The staff role is a custom claim on the ID token, so it is read from there (never trusted from anywhere else).
async function toAuthUser(user: User): Promise<AuthUser> {
  const claim = (await user.getIdTokenResult()).claims.role;
  return { uid: user.uid, phone: user.phoneNumber, email: user.email, role: isStaffRole(claim) ? claim : null };
}

/**
 * Session state for customers. Firebase Auth is only loaded when it is needed: for a
 * visitor who has never signed in on this browser (no auth hint) this does nothing at all,
 * so first-time visitors don't download the Auth SDK just to browse.
 */
export default function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(() => ({
    status: hasAuthHint() ? "loading" : "signed-out",
    user: null,
  }));

  // Whether a Firebase listener should be attached: true from the start for a returning
  // browser, and turned on by a sign-in during this page session (so revocation, a disabled
  // account or a sign-out in another tab are still noticed).
  const [tracking, setTracking] = useState(hasAuthHint);

  useEffect(() => {
    if (!tracking) return;
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    (async () => {
      try {
        const [auth, { onAuthStateChanged }] = await Promise.all([
          getFirebaseAuth(),
          import("firebase/auth"),
        ]);
        if (cancelled) return;
        unsubscribe = onAuthStateChanged(auth, (user) => {
          if (user) {
            void toAuthUser(user).then(
              (authUser) => !cancelled && setState({ status: "signed-in", user: authUser }),
              () => !cancelled && setState({ status: "signed-out", user: null }),
            );
          } else {
            clearAuthHint(); // the saved session is gone (expired / revoked / signed out elsewhere)
            setState({ status: "signed-out", user: null });
          }
        });
      } catch {
        if (!cancelled) setState({ status: "signed-out", user: null });
      }
    })();

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [tracking]);

  // Completes Firebase phone sign-in: throws (auth/invalid-verification-code, …) on a bad code.
  const confirmSmsCode = useCallback(async (confirmation: ConfirmationResult, code: string) => {
    const credential = await confirmation.confirm(code);
    setAuthHint();
    setState({ status: "signed-in", user: await toAuthUser(credential.user) });
    setTracking(true);
  }, []);

  const finishStaff = useCallback(async (user: User) => {
    const authUser = await toAuthUser(user);
    if (!authUser.role) {
      // A valid Firebase account, but not a staff account: never leave it signed in through the staff door.
      await (await getFirebaseAuth()).signOut();
      throw new Error("NOT_STAFF");
    }
    setAuthHint();
    setState({ status: "signed-in", user: authUser });
    setTracking(true);
  }, []);

  const signInStaff = useCallback(
    async (username: string, password: string, remember = true) => {
      const [auth, { signInWithEmailAndPassword, setPersistence, browserLocalPersistence, browserSessionPersistence }] = await Promise.all([getFirebaseAuth(), import("firebase/auth")]);
      // "Keep me signed in on this device": local storage, or just this browser tab/session.
      await setPersistence(auth, remember ? browserLocalPersistence : browserSessionPersistence);
      const credential = await signInWithEmailAndPassword(auth, staffEmail(username), password);
      await finishStaff(credential.user);
    },
    [finishStaff],
  );

  const signInWithToken = useCallback(
    async (customToken: string, remember = true) => {
      const [auth, { signInWithCustomToken, setPersistence, browserLocalPersistence, browserSessionPersistence }] = await Promise.all([getFirebaseAuth(), import("firebase/auth")]);
      await setPersistence(auth, remember ? browserLocalPersistence : browserSessionPersistence);
      const credential = await signInWithCustomToken(auth, customToken);
      await finishStaff(credential.user);
    },
    [finishStaff],
  );

  // If this throws, the session may still exist: the hint and state are left alone so the
  // caller can tell the visitor to retry, instead of claiming they are signed out.
  const signOut = useCallback(async () => {
    const auth = await getFirebaseAuth();
    await auth.signOut();
    clearAuthHint();
    setState({ status: "signed-out", user: null });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, confirmSmsCode, signInStaff, signInWithToken, signOut }),
    [state, confirmSmsCode, signInStaff, signInWithToken, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
